import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const API_URL_PRODUCTS = "https://accessoriesgirly.onrender.com/api/products"; // all products
const API_URL_CATEGORIES = "https://accessoriesgirly.onrender.com/api/categories";

export default function CategoryProductsPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  // Fetch category and products
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch category info
        const catRes = await axios.get(`${API_URL_CATEGORIES}/${categoryId}`);
        setCategory(catRes.data);

        // Fetch all products and filter by category
        const prodRes = await axios.get(API_URL_PRODUCTS);
        const categoryProducts = prodRes.data.filter(
          (p) => p.category._id === categoryId
        );
        setProducts(categoryProducts);
      } catch (err) {
        console.error("Error fetching category/products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categoryId]);

  const updateCart = (updatedCart) => {
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("storage"));
  };

  const addToCart = (item) => {
    const existing = cart.find((c) => c.id === item._id);
    let updatedCart;
    if (existing) {
      updatedCart = cart.map((c) =>
        c.id === item._id ? { ...c, quantity: c.quantity + 1 } : c
      );
    } else {
      updatedCart = [...cart, { ...item, id: item._id, quantity: 1 }];
    }
    updateCart(updatedCart);
  };

  const removeFromCart = (itemId) => {
    const updatedCart = cart
      .map((c) => (c.id === itemId ? { ...c, quantity: c.quantity - 1 } : c))
      .filter((c) => c.quantity > 0);
    updateCart(updatedCart);
  };

  const calculateDiscount = (price, offerPrice) => {
    if (!offerPrice || offerPrice >= price) return 0;
    return Math.round(((price - offerPrice) / price) * 100);
  };

  if (loading) return <p className="text-center text-lg mt-20">Loading...</p>;
  if (!category) return <p className="text-center text-red-600 mt-20">Category not found</p>;

  return (
    <section className="py-12 bg-gradient-to-br from-pink-50 to-purple-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-pink-700 mb-8 text-center">
          {category.name} Products
        </h2>

        {products.length === 0 ? (
          <p className="text-center text-gray-600">No products in this category.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => {
              const inCart = cart.find((c) => c.id === product._id);
              const discount = calculateDiscount(product.price, product.offerPrice);

              return (
                <motion.div
                  key={product._id}
                  className="relative bg-white border border-pink-200 shadow-md rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl cursor-pointer"
                  whileHover={{ scale: 1.03 }}
                >
                  {discount > 0 && (
                    <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {discount}% OFF
                    </span>
                  )}
                  <img
                    src={product.images?.[0] || "https://via.placeholder.com/300x300"}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                    onClick={() => navigate(`/productInfo/${product._id}`)}
                  />
                  <div className="p-4 text-center">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">
                      {product.name}
                    </h3>
                    <p className={`font-bold mb-2 ${product.qty > 0 ? "text-green-600" : "text-red-600"}`}>
                      {product.qty > 0 ? "In Stock" : "Out of Stock"}
                    </p>
                    <p className="text-pink-600 font-bold mb-3">
                      {product.offerPrice && product.offerPrice < product.price ? (
                        <>
                          <span className="line-through text-gray-400 text-sm mr-2">
                            ₹{product.price.toFixed(2)}
                          </span>
                          <span>₹{product.offerPrice.toFixed(2)}</span>
                        </>
                      ) : (
                        <span>₹{product.price.toFixed(2)}</span>
                      )}
                    </p>

                    {inCart ? (
                      <div className="flex justify-center items-center gap-2">
                        <button
                          className="bg-gray-300 px-3 py-1 rounded-lg"
                          onClick={() => removeFromCart(product._id)}
                        >
                          -
                        </button>
                        <span className="font-bold">{inCart.quantity}</span>
                        <button
                          className="bg-pink-600 text-white px-3 py-1 rounded-lg"
                          onClick={() => addToCart(product)}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <motion.button
                        className={`w-full py-2 px-4 text-white text-sm font-semibold rounded-lg ${
                          product.qty > 0 ? "bg-pink-600 hover:bg-pink-700" : "bg-gray-400 cursor-not-allowed"
                        }`}
                        onClick={() => product.qty > 0 && addToCart(product)}
                      >
                        Add to Cart
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
